// Atomic delete các artifact Avatar khỏi project. Gỡ marker block trong
// .gitignore, remove submodule entry trong .gitmodules. Không đụng src/ + git
// history + user config.
import { readFile, rm, writeFile } from "node:fs/promises";
import type { AvatarProjectArtifacts } from "./detect-avatar-project-artifacts.js";
import { AVATAR_MARKER_END, AVATAR_MARKER_START } from "./gitignore-template-loader.js";

export interface UninstallFlags {
  keepSubmodule?: boolean;
  keepHooks?: boolean;
}

export async function executeUninstallDeletion(
  artifacts: AvatarProjectArtifacts,
  flags: UninstallFlags,
): Promise<void> {
  // Delete .claude/ (trừ khi --keep-submodule muốn giữ pack/ — thực tế cả
  // .claude/ chứa nhiều thứ khác, nên --keep-submodule chỉ giữ pack/).
  if (artifacts.claudeDir) {
    if (flags.keepSubmodule) {
      // Chỉ xóa các file/folder không phải pack/ trong .claude/.
      const { readdir } = await import("node:fs/promises");
      const { join } = await import("node:path");
      const entries = await readdir(artifacts.claudeDir);
      for (const entry of entries) {
        if (entry === "pack") continue;
        await rm(join(artifacts.claudeDir, entry), { recursive: true, force: true });
      }
    } else {
      await rm(artifacts.claudeDir, { recursive: true, force: true });
    }
  }

  if (artifacts.claudeMd) {
    await rm(artifacts.claudeMd, { force: true });
  }

  if (!flags.keepHooks) {
    if (artifacts.postMergeHook) await rm(artifacts.postMergeHook, { force: true });
    if (artifacts.prePushHook) await rm(artifacts.prePushHook, { force: true });
  }

  // Strip Avatar block khỏi .gitignore (giữ rest).
  if (artifacts.gitignorePath) {
    await stripAvatarBlockFromGitignore(artifacts.gitignorePath);
  }

  // Remove submodule entry .claude/pack khỏi .gitmodules (nếu xóa cả pack).
  if (artifacts.gitmodulesPath && !flags.keepSubmodule) {
    await removeSubmoduleEntry(artifacts.gitmodulesPath, ".claude/pack");
  }

  // Workspace mode có notes/, scripts/. Chỉ xóa nếu rỗng (user có thể đã add file).
  for (const dir of [artifacts.notesDir, artifacts.scriptsDir]) {
    if (!dir) continue;
    const { readdir } = await import("node:fs/promises");
    const entries = await readdir(dir);
    if (entries.length === 0) {
      await rm(dir, { recursive: true, force: true });
    }
  }
}

async function stripAvatarBlockFromGitignore(path: string): Promise<void> {
  const content = await readFile(path, "utf8");
  const startIdx = content.indexOf(AVATAR_MARKER_START);
  const endIdx = content.indexOf(AVATAR_MARKER_END);
  if (startIdx === -1 || endIdx === -1) return;

  const before = content.slice(0, startIdx);
  const after = content.slice(endIdx + AVATAR_MARKER_END.length);
  const cleaned = `${before.trimEnd()}\n${after.trimStart()}`.trim();
  if (cleaned.length === 0) {
    await rm(path, { force: true });
  } else {
    await writeFile(path, `${cleaned}\n`, "utf8");
  }
}

async function removeSubmoduleEntry(gitmodulesPath: string, submodulePath: string): Promise<void> {
  const content = await readFile(gitmodulesPath, "utf8");
  const lines = content.split("\n");
  const result: string[] = [];
  let skip = false;
  for (const line of lines) {
    if (line.trim().startsWith("[submodule") && line.includes(submodulePath)) {
      skip = true;
      continue;
    }
    // Reset skip khi gặp BẤT KỲ section header INI ("[..."), không chỉ [submodule.
    // Nếu chỉ reset trên [submodule, một section khác (vd [core]) nằm ngay sau
    // entry cần xóa sẽ bị nuốt nhầm → .gitmodules hỏng.
    if (skip && line.trim().startsWith("[")) {
      skip = false;
    }
    if (!skip) result.push(line);
  }
  const cleaned = result.join("\n").trim();
  if (cleaned.length === 0) {
    await rm(gitmodulesPath, { force: true });
  } else {
    await writeFile(gitmodulesPath, `${cleaned}\n`, "utf8");
  }
}
